const isIdentStart = (char) => /[A-Za-z_]/.test(char);
const isIdentPart = (char) => /[A-Za-z0-9_]/.test(char);

const skipUntil = (sql, start, predicate) => {
  let i = start;
  while (i < sql.length && !predicate(sql, i)) {
    i += 1;
  }
  return i;
};

const skipQuoted = (sql, start, quote) => {
  let i = start + 1;
  while (i < sql.length) {
    if (sql[i] === quote) {
      if (sql[i + 1] === quote) {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i += 1;
  }
  return i;
};

/**
 * Extract distinct `@Param` names from a SQL query.
 *
 * Ignores string literals, line/block comments, and system variables
 * such as @@ROWCOUNT / @@DBTS. Repeated @Id tokens collapse to a single
 * name. Returned keys never include `@`.
 *
 * @param {unknown} sql
 * @returns {string[]}
 */
export const extractSqlNamedParameters = (sql) => {
  if (sql == null || typeof sql !== 'string' || sql.length === 0) {
    return [];
  }

  const names = [];
  const seen = new Set();
  let i = 0;

  while (i < sql.length) {
    const char = sql[i];
    const next = sql[i + 1];

    if (char === '-' && next === '-') {
      i = skipUntil(sql, i + 2, (source, index) => source[index] === '\n');
      continue;
    }

    if (char === '/' && next === '*') {
      i += 2;
      while (i < sql.length && !(sql[i] === '*' && sql[i + 1] === '/')) {
        i += 1;
      }
      i = Math.min(sql.length, i + 2);
      continue;
    }

    if (char === "'") {
      i = skipQuoted(sql, i, "'");
      continue;
    }

    if (char === '"') {
      i = skipQuoted(sql, i, '"');
      continue;
    }

    if (char === '`') {
      i = skipQuoted(sql, i, '`');
      continue;
    }

    if (char === '[') {
      i = skipUntil(sql, i + 1, (source, index) => source[index] === ']') + 1;
      continue;
    }

    if (char === '@') {
      if (next === '@') {
        i += 2;
        while (i < sql.length && isIdentPart(sql[i])) {
          i += 1;
        }
        continue;
      }

      if (!isIdentStart(next)) {
        i += 1;
        continue;
      }

      i += 1;
      let name = '';
      while (i < sql.length && isIdentPart(sql[i])) {
        name += sql[i];
        i += 1;
      }

      const key = name.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        names.push(name);
      }
      continue;
    }

    i += 1;
  }

  return names;
};

export default extractSqlNamedParameters;
